import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus, AlertCircle, Heart, ShoppingBag } from "lucide-react";
import { FAI_MONTE_FAETA_LINK } from "../constants";

/**
 * Hero con video di sfondo fullscreen.
 *
 * Per attivare il video definitivo: caricare il file MP4 in
 *   /app/frontend/public/hero-asciano.mp4
 * (max ~5MB, H.264, 1920x1080, senza audio).
 * Se il file non esiste o non si carica, viene mostrata l'immagine
 * /hero-asciano.jpg come poster.
 */
export const Hero = () => {
  const videoRef = useRef(null);
  const [videoOk, setVideoOk] = useState(false);
  const [tshirt, setTshirt] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/products`)
      .then((r) => r.json())
      .then((d) => setTshirt(Array.isArray(d) ? d[0] : null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = async () => {
      try {
        await v.play();
      } catch {
        /* autoplay bloccato: il poster resta visibile, ok */
      }
    };
    tryPlay();
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end sm:items-center pt-20 pb-16 sm:py-32"
    >
      {/* Background: video con fallback immagine */}
      <div className="absolute inset-0 z-0 bg-[#1a2620]">
        {videoOk && (
          <video
            ref={videoRef}
            data-testid="hero-bg-video"
            className="w-full h-full object-cover"
            src="/hero-asciano.mp4"
            poster="/hero-asciano.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            onError={() => setVideoOk(false)}
          />
        )}
        {/* Poster image come fallback (sotto al video, sempre presente) */}
        {!videoOk && (
          <img
            src="/hero-asciano.jpg"
            alt="Asciano e il Monte Faeta"
            className="w-full h-full object-cover"
          />
        )}

        {/* Overlay scuro leggero per leggibilità */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/30" />
        <div className="absolute inset-0 bg-[#1f2d22]/15 mix-blend-multiply" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          {/* Brand mark */}
          <div className="fade-up mb-7 flex items-center gap-3">
            <img
              src="/logo-per-asciano-white.png"
              alt="Per Asciano logo"
              className="h-12 sm:h-14 w-auto opacity-95"
              draggable="false"
            />
            <div className="leading-tight">
              <div className="font-display text-base sm:text-lg font-black tracking-[0.18em] text-white uppercase">
                Per Asciano
              </div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-white/65 mt-1">
                Associazione territoriale · Toscana
              </div>
            </div>
          </div>

          {/* Eyebrow */}
          <div
            className="fade-up inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-4 py-1.5 mb-5"
            style={{ animationDelay: "0.1s" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#E8C9A0] animate-pulse" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              Asciano · Monte Faeta · Toscana
            </span>
          </div>

          {/* Titolo */}
          <h1
            data-testid="hero-title"
            className="fade-up font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.02] tracking-tight"
            style={{ animationDelay: "0.12s" }}
          >
            Rinascita di Asciano
            <br />
            <span className="text-[#E8C9A0] italic font-light">
              e del Monte Faeta.
            </span>
          </h1>

          {/* Sottotitolo */}
          <p
            data-testid="hero-subtitle"
            className="fade-up mt-7 text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-2xl"
            style={{ animationDelay: "0.18s" }}
          >
            Volontari, cittadini e istituzioni insieme per la tutela e la
            valorizzazione del nostro territorio.
          </p>

          {/* CTAs — 3 pulsanti */}
          <div
            className="fade-up mt-9 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
            style={{ animationDelay: "0.28s" }}
          >
            <Link
              to="/partecipa"
              data-testid="hero-volunteer-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] text-white font-bold text-sm sm:text-base px-6 sm:px-7 py-4 hover:bg-[#a64830] transition-all hover:-translate-y-0.5 group shadow-[0_10px_30px_rgba(193,92,61,0.4)]"
            >
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
              Diventa volontario
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>

            <Link
              to="/segnalazioni"
              data-testid="hero-report-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/30 font-semibold text-sm sm:text-base px-6 sm:px-7 py-4 hover:bg-white/20 hover:border-white/50 transition-all"
            >
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              Fai una segnalazione
            </Link>

            <a
              href={FAI_MONTE_FAETA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-fai-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2E4F3B] text-white font-bold text-sm sm:text-base px-6 sm:px-7 py-4 hover:bg-[#1f3828] transition-all hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(46,79,59,0.4)] group"
            >
              <Heart className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
              Firma Monte Faeta
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          {/* Maglietta in evidenza */}
          {tshirt && (
            <Link
              to="/shop"
              data-testid="hero-shop-featured"
              className="fade-up mt-5 group flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/25 p-3 pr-5 max-w-md hover:bg-white/15 hover:border-[#E8C9A0]/60 transition-all"
              style={{ animationDelay: "0.34s" }}
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-white/10 shrink-0 flex items-center justify-center">
                {tshirt.image_url
                  ? <img src={tshirt.image_url} alt={tshirt.name} className="w-full h-full object-cover" draggable="false" />
                  : <ShoppingBag className="w-7 h-7 text-[#E8C9A0]" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E8C9A0]">Novità · Indossa Per Asciano</div>
                <div className="font-display text-base sm:text-lg font-black text-white leading-tight">
                  Maglietta ufficiale · € {Number(tshirt.price).toFixed(2)}
                </div>
                <div className="text-[11px] text-white/70">Ogni acquisto sostiene l'associazione</div>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#E8C9A0] text-[#1f2d22] font-bold text-xs sm:text-sm px-4 py-2 group-hover:gap-2.5 transition-all">
                Acquista <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          )}

          {/* Info row */}
          <div
            className="fade-up mt-10 sm:mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 text-white/65 text-[11px] sm:text-xs uppercase tracking-[0.18em]"
            style={{ animationDelay: "0.4s" }}
          >
            <span className="flex items-center gap-2">
              <span className="w-6 h-px bg-white/40" />
              Associazione Per Asciano
            </span>
            <span className="text-white/30">•</span>
            <span>Volontariato</span>
            <span className="text-white/30">•</span>
            <span>Ambiente</span>
            <span className="text-white/30">•</span>
            <span>Territorio</span>
            <span className="text-white/30">•</span>
            <span>Comunità</span>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/60 text-[10px] tracking-[0.3em] uppercase z-10">
        <span>scorri</span>
        <span className="w-px h-12 bg-white/40 relative overflow-hidden">
          <span className="absolute inset-x-0 top-0 h-4 bg-white animate-[pulse_2s_ease-in-out_infinite]" />
        </span>
      </div>
    </section>
  );
};
